import React, { useState, useEffect, useRef } from 'react';
import { FileText, Upload, Settings, Zap, User, MessageSquare, Edit3, Download, Play, CheckCircle2, AlertCircle, Loader2, X, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import './ProposalDashboard.css';

const ProposalDashboard = () => {
  // Core form data (simplified)
  const [formData, setFormData] = useState({
    clientName: '',
    transcriptText: ''
  });

  // Template and references
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [referenceProposals, setReferenceProposals] = useState([]);
  const [selectedReferenceIds, setSelectedReferenceIds] = useState([]);
  const [referenceSearchTerm, setReferenceSearchTerm] = useState('');
  const [useReferences, setUseReferences] = useState(false);

  // Resources and models
  const [selectedResources, setSelectedResources] = useState({
    psychometrics: [],
    frameworks: [],
    tools: [],
    games: []
  });
  const [selectedMainModel, setSelectedMainModel] = useState('deepseek-v3.1');
  const [selectedDistillModel, setSelectedDistillModel] = useState('deepseek-v3.1');

  // Generation state
  const [generationStatus, setGenerationStatus] = useState('idle');
  const [generatedProposal, setGeneratedProposal] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);

  // Upload proposal (dashboard variant)
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Dashboard state
  const [activeTab, setActiveTab] = useState('client');

  // Resources state
  const [activeResourceCategory, setActiveResourceCategory] = useState('psychometrics');
  const [resourceItems, setResourceItems] = useState([]);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [resourceSearchQuery, setResourceSearchQuery] = useState('');
  const [resourcePage, setResourcePage] = useState(1);
  const [resourceTotalPages, setResourceTotalPages] = useState(1);
  const [resourcePerPage] = useState(12); // Fixed items per page - more for strip layout
  
  // Detail view state
  const [selectedResourceDetail, setSelectedResourceDetail] = useState(null);
  const [showResourceDetail, setShowResourceDetail] = useState(false);
  const [resourceMainTab, setResourceMainTab] = useState('browse'); // 'browse' or 'selected'

  // Editing state
  const [editedContent, setEditedContent] = useState('');
  const [editMode, setEditMode] = useState('ai'); // 'ai' or 'manual'
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatHistory, setAiChatHistory] = useState([]);
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [aiEditEnabled, setAiEditEnabled] = useState(false);

  // Play completion sound (same behavior as old generator, now using .mp3)
  const playGenerationComplete = () => {
    try {
      const audio = new Audio('/sounds/generation-complete.mp3');
      audio.volume = 0.35; // same volume as old implementation
      audio.play().catch(() => {});
    } catch (e) {
      // ignore play errors (autoplay policies, etc.)
    }
  };

  // Download modal state
  const [downloadModal, setDownloadModal] = useState({ visible: false, percent: 0, label: '' });

  // Context estimation state
  const [contextInfo, setContextInfo] = useState({
    total_tokens: 0,
    suggested_model: null,
    context_limit: null,
    utilization: 0,
    all_suitable_models: [],
    free_models: [],
    paid_models: [],
    warning: null
  });

  useEffect(() => {
    fetchReferenceProposals();
  }, []);

  // Context estimation
  const estimateContext = async () => {
    try {
      const data = {
        transcriptText: formData.transcriptText,
        customPrompt: formData.customPrompt || '',
        selectedTemplate: selectedTemplate,
        referenceProposalIds: selectedReferenceIds,
        selectedFrameworks: selectedResources.frameworks,
        selectedTools: selectedResources.tools,
        selectedGames: selectedResources.games,
        selectedPsychometrics: selectedResources.psychometrics
      };

      const response = await apiService.estimateContext(data);
      if (response.success) {
        setContextInfo(response.context_info);
      }
    } catch (error) {
      console.error('Context estimation failed:', error);
    }
  };

  // Estimate context when form data changes
  useEffect(() => {
    if (formData.transcriptText || selectedReferenceIds.length > 0 || 
        selectedResources.frameworks.length > 0 || selectedResources.tools.length > 0 ||
        selectedResources.games.length > 0 || selectedResources.psychometrics.length > 0) {
      estimateContext();
    }
  }, [formData.transcriptText, selectedReferenceIds, selectedResources, selectedTemplate]);

  useEffect(() => {
    if (activeTab === 'resources') {
      loadResourceItems();
    }
  }, [activeTab, activeResourceCategory, resourceSearchQuery, resourcePage]);

  useEffect(() => {
    // Reset page and detail view when category or search changes
    setResourcePage(1);
    setShowResourceDetail(false);
    setSelectedResourceDetail(null);
  }, [activeResourceCategory, resourceSearchQuery]);

  const loadResourceItems = async () => {
    setResourceLoading(true);
    try {
      console.log(`🔍 Loading ${activeResourceCategory} - Page ${resourcePage}`);
      let response;
      if (resourceSearchQuery.trim()) {
        console.log(`🔍 Searching: "${resourceSearchQuery}" in ${activeResourceCategory}`);
        response = await apiService.searchCatalogs(resourceSearchQuery, activeResourceCategory, resourcePage, resourcePerPage);
      } else {
        console.log(`🔍 Loading category: ${activeResourceCategory}`);
        response = await apiService.getCatalogItems(activeResourceCategory, resourcePage, resourcePerPage);
      }
      
      console.log(`✅ Loaded ${response.items?.length || 0} items for ${activeResourceCategory}`);
      console.log(`📊 Total pages: ${response.total_pages}, Current page: ${response.page}`);
      
      setResourceItems(response.items || []);
      setResourceTotalPages(response.total_pages || 1);
    } catch (error) {
      console.error(`❌ Error loading ${activeResourceCategory}:`, error);
      setResourceItems([]);
      setResourceTotalPages(1);
    } finally {
      setResourceLoading(false);
    }
  };

  const handleResourceSelect = (resource, categoryOverride = null) => {
    // Prefer the currently active category when selecting, fallback to resource.category
    const category = categoryOverride || activeResourceCategory || resource.category;
    
    setSelectedResources(prev => {
      const categoryItems = prev[category] || [];
      const isSelected = categoryItems.some(item => item.id === resource.id);
      
      if (isSelected) {
        // Remove resource
        return {
          ...prev,
          [category]: categoryItems.filter(item => item.id !== resource.id)
        };
      } else {
        // Add resource
        return {
          ...prev,
          [category]: [...categoryItems, resource]
        };
      }
    });
  };

  const isResourceSelected = (resource) => {
    const categoryItems = selectedResources[activeResourceCategory] || [];
    return categoryItems.some(item => item.id === resource.id);
  };

  const removeResource = (resourceId, category) => {
    setSelectedResources(prev => ({
      ...prev,
      [category]: prev[category].filter(item => item.id !== resourceId)
    }));
  };

  const handleResourceDetailClick = (resource) => {
    setSelectedResourceDetail(resource);
    setShowResourceDetail(true);
  };

  const handleReturnToList = () => {
    setShowResourceDetail(false);
    setSelectedResourceDetail(null);
  };

  const handleToggleResourceSelection = (resource) => {
    handleResourceSelect(resource, activeResourceCategory);
    // Close detail view after selection
    setShowResourceDetail(false);
    setSelectedResourceDetail(null);
  };

  // Helper function to get total selected count across all categories
  const getTotalSelectedCount = () => {
    return Object.values(selectedResources).reduce((total, resources) => total + resources.length, 0);
  };

  // Test Stage 2 content for debugging
  const testStage2Content = `**Slide 1: Cover Slide**

[Brainayan Logo]

**Leadership Basecamp: Building Trust & Collaboration**
A 1-Day Workshop for dcm

Client: dcm
Date: [To be confirmed]

© 2025 • Brainayan

---

**Slide 2: Context / Why This Workshop?**

**Challenge Statement:**
dcm's leaders are navigating increased complexity, diverse teams, and high-pressure situations without consistent frameworks or tools to foster psychological safety and open dialogue. A pre-engagement diagnostic indicates that 72% of dcm's mid-to-senior level leaders report spending over 40% of their workweek mediating conflicts arising from communication gaps and trust deficits. This is compounded by a 31% increase in cross-functional project initiatives over the past fiscal year, placing unprecedented strain on collaborative frameworks. These challenges are directly affecting strategic alignment, with engagement surveys showing a 15% drop in team morale in high-pressure departments, ultimately impairing the organization's agility and ability to respond to market volatility and internal change.

**Industry Insight:**
A 2024 McKinsey Global Institute report on leadership effectiveness found that organizations with low levels of documented psychological safety experienced 2.8 times slower project completion rates and a 45% higher attrition rate among high-potential employees. Leaders are navigating this increased complexity, diverse teams, and high-pressure situations without consistent frameworks, leading to an estimated $19.8 million in annual productivity loss for Fortune 1000 companies due to collaboration inefficiencies.

**Hook line:**
"This workshop is designed to address these leadership challenges head-on by building foundational skills in trust, communication, and collaboration, directly targeting the 40% of managerial time currently lost to misalignment and rework. We transform that lost time into a strategic investment in cohesive, high-performing teams."

---

**Slide 3: Neuroscience & Psychology Insights**

**Title: "The Science Behind the Workshop"**

-   **Psychological Safety:** Grounded in Amy Edmondson's seminal 1999 research at Harvard, psychological safety creates environments where team members feel safe to take risks and voice opinions without fear of reprisal. Neurochemically, this state reduces amygdala activation (the brain's threat detector) and increases prefrontal cortex activity, enabling higher cognitive functions like problem-solving and creativity. We achieve this through activities designed to trigger oxytocin release, a neuropeptide strongly linked to trust and social bonding.

-   **SCARF Model:** Developed by Dr. David Rock of the NeuroLeadership Institute, this model addresses five key social domains: Status (perception of one's importance), Certainty (ability to predict outcomes), Autonomy (sense of control), Relatedness (feeling of connection to others), and Fairness (perception of impartial treatment). Each workshop module contains mechanisms to minimize perceived threats and maximize rewards in these domains. For example, clear agendas enhance Certainty, while choice in activities boosts Autonomy.

-   **Trust Foundations:** Based on character, communication, and consistency principles, as validated by the Trust Equation (Maister, Green, Galford). Neuroscientifically, consistency and predictability build trust by creating reliable neural patterns. When leaders act inconsistently, it triggers a cortisol response in teams, inhibiting cognitive function. Our frameworks train leaders to create predictable, reliable patterns of behavior.

-   **Behavior Change:** Rooted in experiential learning and immediate application, leveraging the Hebbian principle ("neurons that fire together, wire together"). We utilize Kolb's Experiential Learning Cycle (Concrete Experience, Reflective Observation, Abstract Conceptualization, Active Experimentation) to create new neural pathways. Each learning point is immediately followed by a practical application exercise, ensuring a 70% higher retention rate compared to traditional lecture-based models.

*These insights are embedded in every activity, making learning stick through deliberate neuroplasticity engagement.*

---

**Slide 4: Workshop Objective**

**Headline: "What this workshop enables"**

- Build and maintain trust across diverse teams and functions
- Close communication gaps through empathetic and clear dialogue
- Foster psychological safety for open collaboration and innovation
- Accelerate decision-making through aligned frameworks
- Create cohesive team cultures that respond effectively to change

---

**Slide 5: Design Principles**

**Our Approach:**
-   **Research-backed learning design rooted in neuroscience and psychology:** Our methodology is directly informed by the NeuroLeadership Institute's research on conversational intelligence and the ARCS model of motivational design (Attention, Relevance, Confidence, Satisfaction) by John Keller. This ensures content is not only absorbed but is intrinsically motivating for behavioral adoption.

-   **Highly interactive, practical, and reflective activities:** We employ a 70/20/10 model: 70% of time is spent on experiential application (simulations, role-plays), 20% on social learning (peer feedback, group discussion), and 10% on formal content delivery. This ratio is proven to maximize skill retention and application.

-   **ODI approach (Observe, Discuss, Implement) for real behavior change:** This is a structured iterative cycle. **Observe:** Participants analyze video case studies of effective and ineffective leadership behaviors. **Discuss:** In facilitated small groups, they deconstruct the neuroscience and psychology at play. **Implement:** They immediately practice and role-play the corrected behaviors, receiving real-time feedback.

-   **Super customized to dcm's specific leadership challenges:** All case studies, role-play scenarios, and reflection exercises are built from pre-workshop interviews and the DEIB questionnaire data, ensuring the learning is directly relevant to dcm's operational context and strategic goals.

-   **Focused on sustainable performance and cohesive work culture:** The design includes "commitment contracts" and post-workshop support via the MApp tool to combat the Ebbinghaus Forgetting Curve, ensuring learning is reinforced over a 30-day period to move from short-term memory to long-term behavioral habit.

**Visual:** [Learning methodology wheel: Learn–Apply–Reflect–Commit]
*   **Learn (15 mins):** Bite-sized introduction of a concept (e.g., the 3Cs of Trust) via a micro-lesson.
*   **Apply (40 mins):** A hands-on activity like a trust audit exercise where leaders assess a current team challenge through the 3Cs lens.
*   **Reflect (20 mins):** Structured peer coaching trios where participants share insights and challenges from the application exercise.
*   **Commit (10 mins):** Individuals document one specific action they will take in the next 48 hours to apply the learning, which is logged in their MApp tool.

---

**Slide 6: Workshop Agenda (Full Day)**

**Morning (AM):**
-   **9:00-9:30 (30 mins): Icebreaker & Context Setting**
    *   **Activity:** "Trust Map" exercise - Participants privately map their top two trust builders and breakers from recent experiences, then share in pairs. This immediately personalizes the day's content.
    *   **Objective:** To create a safe container for learning, establish personal relevance, and surface shared challenges.
    *   **Facilitation Note:** Facilitators model vulnerability by sharing their own examples first to establish psychological safety.

-   **9:30-11:00 (90 mins): Module 1: Foundation of Trust (3Cs Trust Framework)**
    *   **9:30-9:50 (20 mins): Learn** - Introduction to the 3Cs (Character, Communication, Consistency) with neuroscience behind why each pillar builds trust.
    *   **9:50-10:30 (40 mins): Apply** - "Trust Audit" - In small groups, participants analyze a provided dcm-specific scenario to identify which C is strongest/weakest and why.
    *   **10:30-10:50 (20 mins): Reflect** - Group debrief on audit findings. Facilitator highlights patterns and connects to brain science.
    *   **10:50-11:00 (10 mins): Commit** - Individuals note one specific trust-building action for an upcoming interaction.

-   **11:00-11:15 (15 mins): Break**
    *   **Transition:** "Coffee Chat" prompt: "Discuss with one person: What's one behavior that would instantly increase trust on your team?"

-   **11:15-12:30 (75 mins): Module 2: Communication for Psychological Safety (SCARF Model application)**
    *   **11:15-11:30 (15 mins): Learn** - Micro-lesson on the SCARF model, explaining how each domain can be a threat or reward in conversation.
    *   **11:30-12:10 (40 mins): Apply** - "SCARF Scenario Roller" - Groups receive a challenging conversation prompt (e.g., giving tough feedback, allocating scarce resources). They must plan the conversation explicitly addressing each SCARF domain for the recipient.
    *   **12:10-12:25 (15 mins): Reflect** - Groups share their approach. Facilitator provides a live critique based on SCARF principles.
    *   **12:25-12:30 (5 mins): Commit** - Quick personal note on one SCARF element to focus on in the afternoon.

**Afternoon (PM):**
-   **12:30-1:30 (60 mins): Lunch**
    *   **Transition:** "Lunch Task" - At tables, discuss: "Where do you see the biggest opportunity to apply SCARF in your function?"

-   **1:30-3:00 (90 mins): Module 3: Cross-Functional Collaboration (Case studies & role-plays)**
    *   **1:30-1:45 (15 mins): Learn** - Introduction to "The Collaboration Loop" - a framework for initiating, executing, and reviewing cross-functional projects.
    *   **1:45-2:25 (40 mins): Apply** - "dcm Collaboration Challenge" - Teams role-play a current, real cross-functional initiative provided by dcm leadership, using the new framework.
    *   **2:25-2:50 (25 mins): Reflect** - Fishbowl debrief: One group replays a key moment of their role-play for the whole room to analyze and learn from.
    *   **2:50-3:00 (10 mins): Commit** - Note one process from the Loop to implement in the next project kickoff.

-   **3:00-3:15 (15 mins): Break**
    *   **Transition:** "Energy Boost" - Quick stand-up and stretch with a reflection question: "What has been your biggest 'aha' moment so far?"

-   **3:15-4:30 (75 mins): Module 4: Real-world Simulation & Peer Practice**
    *   **3:15-3:20 (5 mins): Learn** - Brief on the simulation rules and objectives.
    *   **3:20-4:00 (40 mins): Apply** - "The Alignment Simulation" - A multi-round exercise where teams must negotiate resources, navigate conflicting priorities, and hit a shared goal, applying all day's learnings under time pressure.
    *   **4:00-4:20 (20 mins): Reflect** - Facilitated debrief of the simulation: What trust, communication, and collaboration behaviors worked? What didn't? What does this reveal about our real-world dynamics?
    *   **4:20-4:30 (10 mins): Commit** - Identify one systemic collaboration barrier revealed and a potential solution.

-   **4:30-5:00 (30 mins): Reflection & Action Planning with MApp tool**
    *   **Activity:** Guided individual time to build a personalized "Leadership Action Plan" within the MApp tool. This plan integrates commitments from throughout the day into a sequenced 30-day roadmap with reminders and metrics for success.
    *   **Facilitation:** Coaches circulate to provide support and challenge participants to set specific, measurable goals.

-   **5:00-5:30 (30 mins): Closing & Commitments**
    *   **Activity:** "Public Commitments" - Each participant shares one key action from their plan with the whole group to create accountability.
    *   **Objective:** To create social pressure and support for follow-through, sealing the learning experience.
    *   **Final Note:** Overview of post-workshop support and MApp access.

---

**Slide 7: Learning Experience**

**Participants will experience:**

-   **Interactive trust-building exercises and experiential activities:** For example, the "Trust Fall" is modernized into a "Knowledge Trust" exercise where leaders must rely on information provided by a colleague to complete a task, demonstrating practical reliance and vulnerability in a business context.

-   **Real workplace scenarios from dcm's context:** Scenarios are curated from pre-workshop interviews with dcm stakeholders and include challenges like managing resource allocation conflicts between departments, integrating new agile methodologies into traditional teams, and giving performance feedback to a high-performing but culturally diverse employee.

-   **Role-play sessions for difficult conversations:** Using a structured "COIN" model (Context, Observation, Impact, Next steps) for delivering feedback. Role-plays are conducted in trios (speaker, listener, observer) with detailed observer checklists to provide specific, behavioral feedback on trust and SCARF application.

-   **Group discussions and problem-solving activities:** Utilizing Liberating Structures like "1-2-4-All" to ensure all voices are heard. For example, groups will tackle a problem like "How might we reduce the time spent in cross-functional alignment meetings by 25%?" using the frameworks learned.

-   **Personal reflection and peer feedback sessions:** Structured reflection uses prompts like "What is one leadership behavior you exhibited today that you want to stop, start, or continue?" Peer feedback is guided by the "SBI" model (Situation, Behavior, Impact) to ensure it is constructive and actionable.

-   **Practical tools for immediate workplace application:** Leaders will leave with physical job-aids, including a "Trust Checklist" for team meetings, a "SCARF Quick Scan" for preparing difficult conversations, and digital templates for project collaboration charters, all loaded into their MApp tool for easy access.

---

**Slide 8: Tools & Frameworks**

**Primary Tools:**
-   **3Cs Trust Framework:** A diagnostic and building tool focused on evaluating and strengthening a leader's demonstration of Character (intent and integrity), Communication (clarity and openness), and Consistency (reliability and predictability). Leaders will use a provided scorecard to assess their own behaviors and receive guided peer feedback on a specific recent interaction.

-   **SCARF Model:** A framework for assessing and influencing social threats and rewards in interactions. Participants will learn to use a pre-conversation planning tool to consciously design discussions that minimize threats to a colleague's Status, Certainty, Autonomy, Relatedness, and Fairness, thereby enabling more open dialogue and reducing defensive reactions.

-   **MApp (Manager Awesomeness App):** A mobile and web-based application for ongoing practice and tracking. Its features include: a habit tracker for daily leadership behaviors; a repository of micro-learning videos (2-3 mins) on topics covered; a prompt for weekly reflection on successes and challenges; and a system to set and share goals with an accountability partner.

-   **DEIB Awareness Score Questionnaire:** A validated 20-item pre- and post-workshop assessment that measures leaders' self-reported awareness and behaviors related to fostering inclusivity, equity, and psychological safety within their teams. The pre-assessment data is used to customize workshop examples, and the delta between pre/post scores provides a measure of mindset shift.

-   **Cross-Cultural Communication Exercise:** A practical exercise based on Erin Meyer's "The Culture Map" framework, designed for navigating diverse team dynamics. Participants are assigned different cultural profiles (e.g., direct vs. indirect feedback, linear vs. flexible time) and must collaborate on a task, experiencing firsthand how cultural differences manifest and how to bridge them.

*Tools designed for easy application back at work through simplified job-aids and digital integration.*

---

**Slide 9: Participant Takeaways**

**Leaders will walk away with:**

-   **A personalized trust-building and communication toolkit:** This is not a generic set of tips. It includes their completed 3Cs self-assessment, a SCARF planning template populated with their own upcoming difficult conversations, and a set of reflection notes identifying their personal triggers and patterns in collaboration.

-   **Practical frameworks for fostering psychological safety:** Leaders will be equipped to conduct a "Psychological Safety Thermometer" check at the start of team meetings—a quick, anonymous pulse survey—and will have a structured protocol for addressing the results and co-creating solutions with their team.

-   **Enhanced skills for cross-functional collaboration and decision-making:** They will possess a formalized "Collaboration Charter" template to use for launching new projects, defining communication protocols, decision-rights, and conflict resolution processes upfront, saving significant time and reducing friction during execution.

-   **Personal action plan for immediate implementation:** The plan, built in the MApp tool, will contain specific, time-bound actions for the first week (e.g., "Have one SCARF-planned conversation with X"), the first month (e.g., "Implement the Collaboration Charter for Project Y"), and the first quarter (e.g., "Achieve a 10% improvement in my team's engagement score on psychological safety items").

-   **Access to MApp for ongoing skill development and tracking:** The 30-day access period includes weekly nudges, prompts for reflection, and the ability to track progress against their action plan. This transforms a one-day event into the beginning of a sustained behavioral change journey, significantly increasing the ROI on the training investment.

---

**Slide 10: Program Logistics**

- **Duration:** 1 day (7 hours including breaks)
- **Mode:** [To be confirmed with client - Online/Offline/Hybrid]
- **Group Size:** 15-25 leaders for optimal effectiveness
- **Support:** Pre-workshop assessment (DEIB Questionnaire), post-workshop toolkit, MApp access for 30 days, optional follow-up sessions

---

**Slide 11: Facilitator Profile**

**Lead Facilitator:**
- Brainayan certified leadership development expert with over 12 years of experience specifically designing and delivering workshops on trust-building, psychological safety, and neuroscience-based behavior change.
- Holds certifications in Advanced NeuroLeadership from the NeuroLeadership Institute, Conversational Intelligence (C-IQ)®, and is a licensed practitioner of the Everything DiSC® and The Five Behaviors of a Cohesive Team® models.
- Background includes facilitating over 200 workshops for global clients in the technology and financial services sectors, including a 6-month engagement with a Fortune 50 company that resulted in a measured 18% increase in psychological safety scores and a 22% reduction in project cycle times for participating teams.
- Facilitation style is highly energetic, empathetic, and data-driven, creating a compelling learning environment that balances challenge and support. They specialize in weaving participant real-time experiences into the teachable moments of the day.

**Co-Facilitators:** [To be confirmed based on group size and specific needs] Our co-facilitators are typically ICF-certified coaches or organizational psychologists who assist by managing breakout rooms, providing one-on-one support during exercises, capturing key insights, and ensuring the learning environment remains engaging and productive for all participants.

---

**Slide 12: Closing Slide**

**Thank you for considering Brainayan for your leadership development needs.**

**Next Steps:**
- Schedule discovery call to customize content
- Confirm dates and participant numbers
- Begin pre-workshop assessment process

**Contact:** [To be confirmed with client]

© 2025 • Brainayan`;

  // Initialize edited content with generated proposal content
  useEffect(() => {
    if (generatedProposal) {
      // The actual content is in proposal.content (from backend API structure)
      const content = generatedProposal.proposal?.content || 
                     generatedProposal.stage2_content || 
                     generatedProposal.generated_content || '';
      
      console.log('Setting editedContent from generatedProposal:', content);
      
      if (content && content !== testStage2Content) {
        setEditedContent(content);
      }
    }
  }, [generatedProposal]);

  // AI Chat functions
  const handleAiChatSubmit = async () => {
    if (!aiChatInput.trim()) return;

    const userMessage = aiChatInput.trim();
    setAiChatInput('');
    setIsAiProcessing(true);

    // Add user message to chat history
    const newChatHistory = [...aiChatHistory, { role: 'user', content: userMessage }];
    setAiChatHistory(newChatHistory);

    try {
      // Make actual API call to backend for AI editing
      const response = await apiService.editProposalContent(
        editedContent,
        userMessage,
        'groq-compound' // Always use Groq Compound for chat editing
      );
      
      if (response.data.status === 'success') {
        // Update the actual content with AI-edited version
        setEditedContent(response.data.edited_content);
        
        const aiResponse = `✅ I've updated the content based on your request: "${userMessage}". 

📊 Changes applied:
• Original length: ${response.data.original_length.toLocaleString()} characters
• Edited length: ${response.data.edited_length.toLocaleString()} characters
• Model used: ${response.data.model_used}

The changes are now visible in the content above. You can ask for further adjustments if needed.`;
        
        setAiChatHistory([...newChatHistory, { role: 'assistant', content: aiResponse }]);
      } else {
        throw new Error('Failed to edit content');
      }
      
      setIsAiProcessing(false);
    } catch (error) {
      console.error('AI chat error:', error);
      setAiChatHistory([...newChatHistory, { role: 'assistant', content: 'Sorry, I encountered an error processing your request. Please try again.' }]);
      setIsAiProcessing(false);
    }
  };

  const handleRegeneratePPT = async () => {
    if (!editedContent.trim()) {
      toast.error('No content to regenerate PPT from');
      return;
    }

    try {
      setGenerationStatus('regenerating');
      setProgressMessage('Regenerating PPT with edited content...');
      
      // TODO: Implement actual PPT regeneration with edited content
      // This would call a backend API that takes the edited Stage 2 content
      // and generates a new PPT using the existing distillation and generation pipeline
      
      setTimeout(() => {
        setGenerationStatus('success');
        setProgressMessage('PPT regenerated successfully with your edits!');
        toast.success('PPT regenerated successfully!');
      }, 3000);
      
    } catch (error) {
      setGenerationStatus('error');
      setProgressMessage('Failed to regenerate PPT');
      toast.error('Failed to regenerate PPT');
      console.error('Regeneration error:', error);
    }
  };

  const downloadProposal = async (format = 'pdf') => {
    console.log('Download button clicked for format:', format);
    console.log('Current generatedProposal:', generatedProposal);
    
    if (generatedProposal && generatedProposal.proposal && generatedProposal.proposal.id) {
      try {
        const proposalId = generatedProposal.proposal.id;
        const clientName = generatedProposal.proposal.client_name || 'proposal';
        
        console.log('Downloading with proposalId:', proposalId, 'clientName:', clientName);
        
        // Get the proposal list to find the correct file paths
        const proposalsResponse = await apiService.getProposals();
        const proposal = proposalsResponse.data.proposals.find(p => p.id === proposalId);
        
        if (!proposal) {
          toast.error('Proposal not found in list');
          return;
        }
        
        // Declare variables for filename and download URL
        let filename;
        let downloadUrl;
        
        // Use the correct file path from the proposal data
        if (format === 'pdf' && proposal.pdf_file_path) {
          // Extract filename from full path
          filename = proposal.pdf_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=pdf`;
        } else if (format === 'pptx' && proposal.pptx_file_path) {
          // Extract filename from full path
          filename = proposal.pptx_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=pptx`;
        } else if (format === 'md' && proposal.md_file_path) {
          filename = proposal.md_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=docx`;
        } else if (format === 'txt' && proposal.txt_file_path) {
          filename = proposal.txt_file_path.split('/').pop();
          downloadUrl = `/api/proposals/download/${filename}?format=txt`;
        } else {
          // Fallback to old filename construction if file paths aren't available
          const extension = format === 'pdf' ? '.pdf' : format === 'txt' ? '.txt' : format === 'md' ? '' : '.pptx';
          filename = `${clientName}_Training_Proposal${extension}`;
          downloadUrl = `/api/proposals/download/${filename}?format=${format === 'md' ? 'docx' : format}`;
        }
        
        console.log('Using filename:', filename);
        console.log('Using download URL:', downloadUrl);
        
        // Show progress modal
        setDownloadModal({ visible: true, percent: 1, label: `${format.toUpperCase()} download starting...` });

        // Download using apiService with progress
        const response = await apiService.downloadProposalWrapped(filename, format, (p) => {
          setDownloadModal(prev => ({ ...prev, percent: Math.max(1, p), label: `${format.toUpperCase()} downloading... ${p}%` }));
        });
        console.log('Download response:', response);
        console.log('Response status:', response.status);
        console.log('Response ok:', response.ok);
        
        // Check if response is successful (200-299 range)
        if (response.status < 200 || response.status >= 300) {
          throw new Error(`Download failed: ${response.status} ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${clientName}_Training_Proposal.${format === 'md' ? 'docx' : format}`;
        
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success(`Download started (${format.toUpperCase()} format)`);
        setDownloadModal({ visible: false, percent: 0, label: '' });
      } catch (error) {
        console.error('Download error:', error);
        toast.error(`Failed to download proposal (${format.toUpperCase()} format): ${error.message}`);
        setDownloadModal({ visible: false, percent: 0, label: '' });
      }
    } else {
      console.error('No proposal available for download. generatedProposal:', generatedProposal);
      toast.error('No proposal available for download');
    }
  };

  // PPTX v2 direct transfer download (separate backend endpoint)
  const downloadProposalV2 = async () => {
    if (!(generatedProposal && generatedProposal.proposal && generatedProposal.proposal.id)) {
      toast.error('No proposal available for download');
      return;
    }
    try {
      const clientName = generatedProposal.proposal.client_name || 'proposal';
      const filename = `${clientName}_Training_Proposal.pptx`;
      const templateParam = selectedTemplate && selectedTemplate !== 'default' ? `&template=${encodeURIComponent(selectedTemplate)}` : '';
      const apiBase = (process.env.REACT_APP_BACKEND_URL || 'https://propulseapi.brainayan.com').replace(/\/$/, '');
      const cacheBust = `&_ts=${Date.now()}`;
      const url = `${apiBase}/api/proposals/download-v2/${encodeURIComponent(filename)}?format=pptx${templateParam}${cacheBust}`;

      setDownloadModal({ visible: true, percent: 1, label: 'PPTX download starting...' });

      const response = await fetch(url, { cache: 'no-store', headers: { 'Accept': 'application/vnd.openxmlformats-officedocument.presentationml.presentation' } });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const contentLength = response.headers.get('Content-Length');
      if (response.body && contentLength) {
        const total = parseInt(contentLength, 10);
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length || value.byteLength || 0;
          const percent = Math.min(99, Math.floor((received / total) * 100));
          setDownloadModal(prev => ({ ...prev, percent, label: `PPTX downloading... ${percent}%` }));
        }
        const blob = new Blob(chunks, { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${clientName}_Training_Proposal_v2.pptx`;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `${clientName}_Training_Proposal_v2.pptx`;
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      }
      toast.success('Download started (PPTX v2)');
      setDownloadModal({ visible: false, percent: 0, label: '' });
    } catch (error) {
      console.error('PPTX v2 download error:', error);
      toast.error(`Failed to download PPTX v2: ${error.message}`);
      setDownloadModal({ visible: false, percent: 0, label: '' });
    }
  };

  const fetchReferenceProposals = async () => {
    try {
      const response = await apiService.getReferenceProposals();
      setReferenceProposals(response.proposals || []);
    } catch (error) {
      console.error('Error fetching reference proposals:', error);
      toast.error('Failed to load reference proposals');
    }
  };

  // File upload handlers (reuse backend endpoint)
  const handleFileUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const allowed = ['.pptx', '.pdf', '.docx'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!allowed.includes(ext)) {
      toast.error('Please select a .pptx, .pdf, or .docx file');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      toast.error('File size must be less than 200MB');
      return;
    }
    setUploadFile(file);
  };

  const uploadProposalFile = async () => {
    if (!uploadFile) return;
    setIsUploading(true);
    try {
      const res = await apiService.uploadProposalForExtraction(uploadFile);
      toast.success(`Uploaded: ${res.filename} (${res.total_slides} slides)`);
      await fetchReferenceProposals();
      setUploadFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err?.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleReferenceSelection = (proposalId) => {
    setSelectedReferenceIds(prev => {
      if (prev.includes(proposalId)) {
        return prev.filter(id => id !== proposalId);
      } else {
        return [...prev, proposalId];
      }
    });
  };

  const validateForm = () => {
    if (!formData.clientName.trim()) {
      toast.error('Please enter a client name');
      return false;
    }
    if (!formData.transcriptText.trim()) {
      toast.error('Please provide transcript text');
      return false;
    }
    return true;
  };

  const generateProposal = async () => {
    if (!validateForm()) return;

    try {
      setGenerationStatus('generating');
      setGenerationProgress(0);
      setProgressMessage('Initializing proposal generation...');
      setSessionId(null);
      
      const payload = {
        clientName: formData.clientName,
        transcriptText: formData.transcriptText,
        selectedTemplate: selectedTemplate === 'default' ? undefined : selectedTemplate,
        referenceProposalIds: selectedReferenceIds.length > 0 ? selectedReferenceIds : undefined,
        use_references: useReferences,
        selected_psychometrics: selectedResources.psychometrics,
        selected_frameworks: selectedResources.frameworks,
        selected_tools: selectedResources.tools,
        selected_games: selectedResources.games,
        selectedMainModel: selectedMainModel,
        selectedDistillModel: selectedDistillModel,
      };
      
      const response = await apiService.generateProposal(payload);
      
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
        pollProgress(response.data.session_id);
        setActiveTab('generation'); // Switch to generation tab
      } else {
        setGeneratedProposal(response.data);
        setGenerationStatus('success');
        setGenerationProgress(100);
        setProgressMessage('Proposal generated successfully!');
        toast.success('Proposal generated successfully!');
        playGenerationComplete();
      }
    } catch (error) {
      setGenerationStatus('error');
      toast.error('Failed to generate proposal');
      console.error('Generation error:', error);
    }
  };

  // Helper to render value or red X when unset
  const renderReviewValue = (value) => {
    const normalized = typeof value === 'string' ? value.trim() : value;
    if (!normalized || normalized === '-' || (Array.isArray(normalized) && normalized.length === 0)) {
      return (
        <span className="review-unset">
          <XCircle size={16} />
        </span>
      );
    }
    return <span>{value}</span>;
  };

  const pollProgress = async (sessionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const progressResponse = await apiService.getGenerationProgress(sessionId);
        const { progress, message, status } = progressResponse.data;
        
        setGenerationProgress(progress);
        setProgressMessage(message);
        
        if (status === 'completed') {
          clearInterval(pollInterval);
          
          const resultResponse = await apiService.getGenerationResult(sessionId);
          console.log('Dashboard - Full generation result:', resultResponse.data);
          console.log('Dashboard - Proposal content:', resultResponse.data.proposal?.content);
          setGeneratedProposal(resultResponse.data);
          setGenerationStatus('success');
          setProgressMessage('Proposal generated successfully!');
          toast.success('Proposal generated successfully!');
          playGenerationComplete();
        } else if (status === 'failed') {
          clearInterval(pollInterval);
          setGenerationStatus('error');
          toast.error('Proposal generation failed');
        }
      } catch (error) {
        clearInterval(pollInterval);
        setGenerationStatus('error');
        toast.error('Error checking generation progress');
      }
    }, 2000);
  };

  const filteredReferenceProposals = referenceProposals.filter(proposal =>
    proposal.title.toLowerCase().includes(referenceSearchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData({ clientName: '', transcriptText: '' });
    setGeneratedProposal(null);
    setGenerationStatus('idle');
    setGenerationProgress(0);
    setProgressMessage('');
    setSessionId(null);
    setSelectedReferenceIds([]);
    setReferenceSearchTerm('');
    setActiveTab('client');
  };

  const tabs = [
    { id: 'client', label: 'Client & Transcript', icon: User },
    { id: 'template', label: 'Template & References', icon: FileText },
    { id: 'resources', label: 'Training Resources', icon: Settings },
    { id: 'models', label: 'Model Selection', icon: MessageSquare },
    { id: 'generation', label: 'Generation', icon: Zap }
  ];

  return (
    <div className="dashboard-container">
      {/* Left Sidebar */}
      <div className="dashboard-sidebar">
        <div className="sidebar-header">
          <h2>Generate Proposal</h2>
        </div>
        
        <nav className="sidebar-nav">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              >
                <Icon className="tab-icon" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Right Content Panel */}
      <div className="dashboard-content">
        {downloadModal.visible && (
          <div className="download-modal-backdrop">
            <div className="download-modal">
              <h4>Preparing download...</h4>
              <div className="modal-subtext">{downloadModal.label || 'This may take a moment for large files.'}</div>
              <div className="download-progress-bar">
                <div className="download-progress-fill" style={{ width: `${downloadModal.percent || 1}%` }} />
              </div>
              <div className="modal-actions">
                <button className="download-cancel-btn" onClick={() => setDownloadModal({ visible: false, percent: 0, label: '' })}>Hide</button>
              </div>
            </div>
          </div>
        )}
        {/* Client & Transcript Tab */}
        {activeTab === 'client' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h3>Client Information & Requirements</h3>
              <p>Provide required details: requirements and context (notes, chat transcripts, written info)</p>
            </div>
            
            <div className="form-section">
              <label className="form-label">
                Client Name <span className="required">*</span>
              </label>
              <input
                type="text"
                name="clientName"
                value={formData.clientName}
                onChange={handleInputChange}
                placeholder="Enter client name"
                className="form-input"
              />
            </div>

            <div className="form-section">
              <label className="form-label">
                Requirements & Context <span className="required">*</span>
              </label>
              <textarea
                name="transcriptText"
                value={formData.transcriptText}
                onChange={handleInputChange}
                placeholder="Enter requirements and context: notes, chat transcript, written info, constraints, links, etc."
                rows={12}
                className="form-textarea"
              />
            </div>
          </div>
        )}

        {/* Template & References Tab */}
        {activeTab === 'template' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h3>Template & Reference Proposals</h3>
              <p>Select proposal template and reference materials</p>
            </div>

            <div className="form-section">
              <label className="form-label">Proposal Template</label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="form-select"
              >
                <option value="default">Standard Template (Default)</option>
                <option value="consultation_project">Consultation Project</option>
                <option value="offsite_team_building">Offsite Team Building</option>
                <option value="leadership_journey">Leadership Journey</option>
                <option value="one_day_workshop">1-Day Workshop</option>
                <option value="concept_note">Concept Note / Approach Deck</option>
              </select>
            </div>

            <div className="form-section">
              <div className="section-header">
                <label className="form-label">Reference Proposals</label>
                <div className="reference-toggle">
                  <input
                    type="checkbox"
                    checked={useReferences}
                    onChange={(e) => setUseReferences(e.target.checked)}
                    className="toggle-input"
                  />
                  <span>Use past proposals as references</span>
                </div>
              </div>

              {useReferences && (
                <div className="references-section">
                  <input
                    type="text"
                    placeholder="Search proposals..."
                    value={referenceSearchTerm}
                    onChange={(e) => setReferenceSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  
                  <div className="proposals-list">
                    {filteredReferenceProposals.length === 0 ? (
                      <div className="empty-state">
                        {referenceSearchTerm ? 'No proposals match your search' : 'Loading proposals...'}
                      </div>
                    ) : (
                      filteredReferenceProposals.map((proposal) => (
                        <label key={proposal.id} className="proposal-item">
                          <input
                            type="checkbox"
                            checked={selectedReferenceIds.includes(proposal.id)}
                            onChange={() => handleReferenceSelection(proposal.id)}
                            className="proposal-checkbox"
                          />
                          <span className="proposal-title">{proposal.title}</span>
                        </label>
                      ))
                    )}
                  </div>

                  {selectedReferenceIds.length > 0 && (
                    <button
                      onClick={() => setSelectedReferenceIds([])}
                      className="clear-button"
                    >
                      Clear All Selections
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Upload New Proposal (dashboard variant) */}
            <div className="form-section">
              <label className="form-label">Upload New Proposal</label>
              <div className="upload-row">
                <input
                  ref={fileInputRef}
                  id="dashboard-proposal-upload"
                  type="file"
                  accept=".pptx,.pdf,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <label htmlFor="dashboard-proposal-upload" className="file-choose">
                  {uploadFile ? uploadFile.name : 'Choose file...'}
                </label>
                {uploadFile && (
                  <button
                    onClick={uploadProposalFile}
                    disabled={isUploading}
                    className="upload-button"
                  >
                    {isUploading ? 'Uploading...' : 'Upload'}
                  </button>
                )}
              </div>
              {uploadFile && (
                <div className="help-text">
                  File: {uploadFile.name} ({(uploadFile.size / 1024 / 1024).toFixed(2)} MB)
                </div>
              )}
            </div>
          </div>
        )}

        {/* Training Resources Tab */}
        {activeTab === 'resources' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h3>Training Resources</h3>
              <p>Select psychometrics, frameworks, tools, and games</p>
            </div>

            {/* Main Resource Tabs */}
            <div className="resource-main-tabs">
              <button
                onClick={() => setResourceMainTab('browse')}
                className={`main-tab ${resourceMainTab === 'browse' ? 'active' : ''}`}
              >
                Browse Resources
              </button>
              <button
                onClick={() => setResourceMainTab('selected')}
                className={`main-tab ${resourceMainTab === 'selected' ? 'active' : ''}`}
              >
                Selected Resources ({getTotalSelectedCount()})
              </button>
            </div>

            {resourceMainTab === 'browse' ? (
              <>
                {/* Resource Category Tabs */}
                <div className="resource-tabs">
              {[
                { key: 'psychometrics', label: 'Psychometrics', icon: '🧠' },
                { key: 'frameworks', label: 'Frameworks', icon: '📋' },
                { key: 'tools', label: 'Tools', icon: '🛠️' },
                { key: 'games', label: 'Games', icon: '🎮' }
              ].map(category => (
                <button
                  key={category.key}
                  onClick={() => setActiveResourceCategory(category.key)}
                  className={`resource-tab ${activeResourceCategory === category.key ? 'active' : ''}`}
                >
                  <span className="tab-icon">{category.icon}</span>
                  <span>{category.label}</span>
                  <span className="tab-count">
                    {selectedResources[category.key]?.length || 0}
                  </span>
                </button>
              ))}
            </div>

            {/* Search Bar */}
            <div className="resource-search">
              <input
                type="text"
                placeholder={`Search ${activeResourceCategory}...`}
                value={resourceSearchQuery}
                onChange={(e) => setResourceSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Selected Resources Summary */}
            {selectedResources[activeResourceCategory]?.length > 0 && (
              <div className="selected-resources">
                <h5>Selected {activeResourceCategory}:</h5>
                <div className="selected-items">
                  {selectedResources[activeResourceCategory].map((resource, index) => (
                    <div key={`selected-${activeResourceCategory}-${resource.id}-${index}`} className="selected-item">
                      <span className="item-name">{resource.name}</span>
                      <button
                        onClick={() => removeResource(resource.id, activeResourceCategory)}
                        className="remove-button"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Resource Items Container with Slide Transition */}
            <div className="resource-items">
              <div className={`resource-content ${showResourceDetail ? 'show-detail' : 'show-list'}`}>
                {/* List View */}
                <div className="list-view">
                  {resourceLoading ? (
                    <div className="loading-state">
                      <Loader2 className="spin" />
                      <span>Loading {activeResourceCategory}...</span>
                    </div>
                  ) : resourceItems.length === 0 ? (
                    <div className="empty-state">
                      {resourceSearchQuery ? 
                        `No ${activeResourceCategory} match your search` : 
                        `No ${activeResourceCategory} available`
                      }
                    </div>
                  ) : (
                    <>
                      {/* Items List */}
                      <div className="items-list">
                        {resourceItems.map((item, index) => (
                          <div
                            key={`${activeResourceCategory}-${resourcePage}-${item.id}-${index}`}
                            onClick={() => handleResourceDetailClick(item)}
                            className={`resource-strip ${isResourceSelected(item) ? 'selected' : ''}`}
                          >
                            <div className="strip-main">
                              <div className="strip-header">
                                <h6 className="strip-title">{item.name}</h6>
                                {isResourceSelected(item) && (
                                  <span className="selected-badge">✓</span>
                                )}
                              </div>
                              <p className="strip-description">{item.what}</p>
                            </div>
                            
                            <div className="strip-meta">
                              {item.industry && (
                                <span className="meta-tag">{item.industry}</span>
                              )}
                              {item.duration && (
                                <span className="meta-tag">{item.duration}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pagination Controls */}
                      {resourceTotalPages > 1 && (
                        <div className="pagination-controls">
                          <div className="pagination-info">
                            Page {resourcePage} of {resourceTotalPages}
                          </div>
                          
                          <div className="pagination-buttons">
                            <button
                              onClick={() => setResourcePage(prev => Math.max(1, prev - 1))}
                              disabled={resourcePage === 1}
                              className="pagination-button"
                            >
                              Previous
                            </button>
                            
                            <div className="page-numbers">
                              {Array.from({ length: Math.min(5, resourceTotalPages) }, (_, i) => {
                                const startPage = Math.max(1, resourcePage - 2);
                                const pageNum = startPage + i;
                                
                                if (pageNum > resourceTotalPages) return null;
                                
                                return (
                                  <button
                                    key={pageNum}
                                    onClick={() => setResourcePage(pageNum)}
                                    className={`page-number ${resourcePage === pageNum ? 'active' : ''}`}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                            </div>
                            
                            <button
                              onClick={() => setResourcePage(prev => Math.min(resourceTotalPages, prev + 1))}
                              disabled={resourcePage === resourceTotalPages}
                              className="pagination-button"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Detail View */}
                <div className="detail-view">
                  {selectedResourceDetail && (
                    <div className="detail-content">
                      <div className="detail-header">
                        <button
                          onClick={handleReturnToList}
                          className="return-button"
                        >
                          ← Back to {activeResourceCategory}
                        </button>
                        <button
                          onClick={() => handleToggleResourceSelection(selectedResourceDetail)}
                          className={`selection-button-inline ${isResourceSelected(selectedResourceDetail) ? 'selected' : ''}`}
                        >
                          {isResourceSelected(selectedResourceDetail) ? '✓ Selected' : 'Select This Resource'}
                        </button>
                      </div>

                      <div className="detail-body">
                        <div className="detail-title-section">
                          <h4 className="detail-title">{selectedResourceDetail.name}</h4>
                          <div className="detail-tags">
                            {selectedResourceDetail.industry && (
                              <span className="detail-tag">{selectedResourceDetail.industry}</span>
                            )}
                            {selectedResourceDetail.duration && (
                              <span className="detail-tag">{selectedResourceDetail.duration}</span>
                            )}
                          </div>
                        </div>

                        <div className="detail-sections">
                          <div className="detail-section">
                            <h5>What</h5>
                            <p>{selectedResourceDetail.what}</p>
                          </div>

                          <div className="detail-section">
                            <h5>Why</h5>
                            <p>{selectedResourceDetail.why}</p>
                          </div>

                          {selectedResourceDetail.how && (
                            <div className="detail-section">
                              <h5>How</h5>
                              <p>{selectedResourceDetail.how}</p>
                            </div>
                          )}

                          {selectedResourceDetail.detailed_explanation && (
                            <div className="detail-section">
                              <h5>Detailed Explanation</h5>
                              <p>{selectedResourceDetail.detailed_explanation}</p>
                            </div>
                          )}

                          {selectedResourceDetail.use_cases && (
                            <div className="detail-section">
                              <h5>Use Cases</h5>
                              <p>{selectedResourceDetail.use_cases}</p>
                            </div>
                          )}

                          {selectedResourceDetail.target_audience && (
                            <div className="detail-section">
                              <h5>Target Audience</h5>
                              <p>{selectedResourceDetail.target_audience}</p>
                            </div>
                          )}

                          {selectedResourceDetail.materials_needed && (
                            <div className="detail-section">
                              <h5>Materials Needed</h5>
                              <p>{selectedResourceDetail.materials_needed}</p>
                            </div>
                          )}

                          {selectedResourceDetail.setup_requirements && (
                            <div className="detail-section">
                              <h5>Setup Requirements</h5>
                              <p>{selectedResourceDetail.setup_requirements}</p>
                            </div>
                          )}

                          {selectedResourceDetail.expected_outcomes && (
                            <div className="detail-section">
                              <h5>Expected Outcomes</h5>
                              <p>{selectedResourceDetail.expected_outcomes}</p>
                            </div>
                          )}

                          {selectedResourceDetail.facilitation_notes && (
                            <div className="detail-section">
                              <h5>Facilitation Notes</h5>
                              <p>{selectedResourceDetail.facilitation_notes}</p>
                            </div>
                          )}

                          {selectedResourceDetail.variations && (
                            <div className="detail-section">
                              <h5>Variations</h5>
                              <p>{selectedResourceDetail.variations}</p>
                            </div>
                          )}

                          {selectedResourceDetail.references && (
                            <div className="detail-section">
                              <h5>References</h5>
                              <p>{selectedResourceDetail.references}</p>
                            </div>
                          )}
                        </div>

                        <div className="detail-actions">
                          <button
                            onClick={() => handleToggleResourceSelection(selectedResourceDetail)}
                            className={`selection-button ${isResourceSelected(selectedResourceDetail) ? 'selected' : ''}`}
                          >
                            {isResourceSelected(selectedResourceDetail) ? 
                              '✓ Select This Resource' : 
                              'Select This Resource'
                            }
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
              </>
            ) : (
              /* Selected Resources View */
              <div className="selected-resources-view">
                {getTotalSelectedCount() === 0 ? (
                  <div className="empty-state">
                    <p>No resources selected yet.</p>
                    <p>Switch to "Browse Resources" to select some resources.</p>
                  </div>
                ) : (
                  <div className="selected-categories">
                    {Object.entries(selectedResources).map(([category, resources]) => 
                      resources.length > 0 && (
                        <div key={category} className="selected-category-section">
                          <div className="category-header">
                            <h4>
                              {category === 'psychometrics' ? '🧠 Psychometrics' :
                               category === 'frameworks' ? '📋 Frameworks' :
                               category === 'tools' ? '🛠️ Tools' :
                               category === 'games' ? '🎮 Games' : category}
                              <span className="count">({resources.length})</span>
                            </h4>
                          </div>
                          <div className="selected-items-grid">
                            {resources.map((resource, index) => (
                              <div key={`selected-view-${category}-${resource.id}-${index}`} className="selected-resource-card">
                                <div className="resource-card-header">
                                  <h6 className="resource-name">{resource.name}</h6>
                                  <button
                                    onClick={() => removeResource(resource.id, category)}
                                    className="remove-btn"
                                    title="Remove resource"
                                  >
                                    ×
                                  </button>
                                </div>
                                <p className="resource-description">{resource.description || 'No description available'}</p>
                                {resource.tags && resource.tags.length > 0 && (
                                  <div className="resource-tags">
                                    {resource.tags.slice(0, 3).map((tag, tagIndex) => (
                                      <span key={tagIndex} className="resource-tag">{tag}</span>
                                    ))}
                                    {resource.tags.length > 3 && <span className="tag-more">+{resource.tags.length - 3} more</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Model Selection Tab */}
        {activeTab === 'models' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h3>AI Model Selection</h3>
              <p>Choose AI models for content generation and distillation</p>
            </div>

            <div className="form-section">
              <label className="form-label">Model Configuration</label>
              <div className="model-grid">
                <div className="model-field">
                  <label>Main Model (Stage 1 & 2)</label>
                  <select
                    value={selectedMainModel}
                    onChange={(e) => setSelectedMainModel(e.target.value)}
                    className="form-select"
                  >
                    <option value="deepseek-v3.1">DeepSeek v3.1</option>
                    <option value="qwen/qwen-max">Qwen Max</option>
                    <option value="meta-llama-4">Meta Llama 4</option>
                    <option value="gpt-oss-20b">GPT-OSS-20B</option>
                    <option value="qwen3-thinking">Qwen3 Thinking</option>
                    <option value="x-ai/grok-4-fast:free">Grok-4-Fast (Free)</option>
                  </select>
                </div>
                
                <div className="model-field">
                  <label>Distillation Model</label>
                  <select
                    value={selectedDistillModel}
                    onChange={(e) => setSelectedDistillModel(e.target.value)}
                    className="form-select"
                  >
                    <option value="deepseek-v3.1">DeepSeek v3.1</option>
                    <option value="qwen/qwen-max">Qwen Max</option>
                    <option value="meta-llama-4">Meta Llama 4</option>
                    <option value="gpt-oss-20b">GPT-OSS-20B</option>
                    <option value="qwen3-thinking">Qwen3 Thinking</option>
                    <option value="x-ai/grok-4-fast:free">Grok-4-Fast (Free)</option>
                  </select>
                </div>
              </div>
              
              <div className="model-info">
                <div className="info-card">
                  <h5>Main Model</h5>
                  <p>Used for Stage 1 (basic proposal) and Stage 2 (refinement). Higher creativity for content generation.</p>
                </div>
                <div className="info-card">
                  <h5>Distillation Model</h5>
                  <p>Used for Stage 3 (PowerPoint distillation). Optimized for structured JSON output with temperature 0.1.</p>
                </div>
              </div>

              {/* Context Information */}
              {contextInfo.total_tokens > 0 && (
                <div className="context-info">
                  <div className="context-header">
                    <h4>Context Usage</h4>
                    <div className="context-stats">
                      <span className="token-count">
                        {contextInfo.total_tokens.toLocaleString()} tokens
                      </span>
                      {contextInfo.context_limit && (
                        <span className="token-limit">
                          / {contextInfo.context_limit.toLocaleString()}
                        </span>
                      )}
                      {contextInfo.utilization > 0 && (
                        <span className="utilization">
                          ({contextInfo.utilization}%)
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {contextInfo.suggested_model && (
                    <div className="model-suggestion">
                      <span className="suggestion-label">Recommended Model:</span>
                      <span className="suggestion-model">{contextInfo.suggested_model}</span>
                    </div>
                  )}

                  {/* All Suitable Models */}
                  {contextInfo.all_suitable_models && contextInfo.all_suitable_models.length > 0 && (
                    <div className="all-models-section">
                      <h5>All Suitable Models ({contextInfo.all_suitable_models.length} available)</h5>
                      <div className="models-grid">
                        {contextInfo.all_suitable_models.map((model, index) => (
                          <div 
                            key={model.model} 
                            className={`model-option ${model.model === contextInfo.suggested_model ? 'selected' : ''} ${contextInfo.free_models.some(m => m.model === model.model) ? 'free' : 'paid'}`}
                          >
                            <div className="model-name">{model.model}</div>
                            <div className="model-stats">
                              <span className="utilization">{model.utilization}%</span>
                              <span className="context-limit">({model.context_limit.toLocaleString()})</span>
                            </div>
                            {contextInfo.free_models.some(m => m.model === model.model) && (
                              <div className="free-badge">Free</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {contextInfo.warning && (
                    <div className="context-warning">
                      <AlertCircle size={16} />
                      <span>{contextInfo.warning}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Generation Tab */}
        {activeTab === 'generation' && (
          <div className="tab-panel">
            <div className="panel-header">
              <h3>Generate Proposal</h3>
              <p>Generate proposal with the selected customizations</p>
            </div>

            {/* Review Summary */}
            <div className="review-summary">
              <div className="review-row">
                <span className="review-label">Client</span>
                <span className="review-value">{renderReviewValue(formData.clientName || '-')}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Requirements & Context</span>
                <span className="review-value review-multiline">{renderReviewValue(formData.transcriptText || '-')}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Template</span>
                <span className="review-value">{renderReviewValue(selectedTemplate || '-')}</span>
              </div>
              <div className="review-row">
                <span className="review-label">Past Proposals</span>
                <span className="review-value">
                  {renderReviewValue(
                    (selectedReferenceIds && selectedReferenceIds.length > 0)
                      ? selectedReferenceIds.map(id => {
                          const p = referenceProposals.find(r => r.id === id);
                          return p ? p.title : id;
                        }).join(', ')
                      : '-'
                  )}
                </span>
              </div>
              <div className="review-row">
                <span className="review-label">Selected Resources</span>
                <span className="review-value">
                  {(() => {
                    const parts = [];
                    const catNames = { psychometrics: 'Psychometrics', frameworks: 'Frameworks', tools: 'Tools', games: 'Games' };
                    Object.keys(selectedResources).forEach(cat => {
                      const arr = selectedResources[cat] || [];
                      if (arr.length > 0) {
                        parts.push(`${catNames[cat]}: ${arr.map(x => x.name || x.title || x.id).join('; ')}`);
                      }
                    });
                    return renderReviewValue(parts.length ? parts.join(' | ') : '-');
                  })()}
                </span>
              </div>
              <div className="review-row">
                <span className="review-label">AI Model</span>
                <span className="review-value">
                  {renderReviewValue(`Main: ${selectedMainModel || '-'} | Distillation: ${selectedDistillModel || '-'}`)}
                </span>
              </div>
            </div>


            {/* Generation Controls */}
            <div className="generation-section">
              <div className="generation-controls">
                <button
                  onClick={generateProposal}
                  disabled={generationStatus === 'generating'}
                  className={`generate-button ${generationStatus === 'generating' ? 'generating' : ''}`}
                >
                  {generationStatus === 'generating' ? (
                    <>
                      <Loader2 className="spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Play />
                      Generate Proposal
                    </>
                  )}
                </button>

                {generationStatus !== 'idle' && (
                  <button onClick={resetForm} className="reset-button">
                    Reset
                  </button>
                )}
              </div>

              {/* Progress Tracking */}
              {generationStatus === 'generating' && (
                <div className="progress-section">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${generationProgress}%` }}
                    />
                  </div>
                  <p className="progress-message">{progressMessage}</p>
                </div>
              )}

              {/* Generation Status */}
              {generationStatus === 'success' && (
                <div className="status-success">
                  <CheckCircle2 />
                  <span>Proposal generated successfully!</span>
                </div>
              )}

              {generationStatus === 'error' && (
                <div className="status-error">
                  <AlertCircle />
                  <span>Generation failed. Please try again.</span>
                </div>
              )}
            </div>

            {/* Generated Proposal Content Display */}
            {generatedProposal && (
              <div className="output-section">
                <div className="output-header">
                  <h4>Generated Proposal Content</h4>
                </div>

                <div className="content-display">
                  <div className="content-text">
                    {editedContent || generatedProposal?.proposal?.content || generatedProposal?.stage2_content || generatedProposal?.generated_content || 'Loading content...'}
                  </div>
                </div>

                {/* Download Actions - Always visible when proposal exists */}
                <div className="download-section">
                  <div className="download-buttons-grid">
                    <button
                      onClick={() => downloadProposal('pdf')}
                      className="download-button primary"
                    >
                      <Download />
                      Download PDF
                    </button>
                    <button
                      onClick={() => downloadProposal('md')}
                      className="download-button primary"
                    >
                      <FileText />
                      Download DOCX
                    </button>
                    {/**
                    <button
                      onClick={() => downloadProposal('pptx')}
                      className="download-button primary"
                    >
                      <Download />
                      Download PPTX
                    </button>
                    **/}
                    <button
                      onClick={downloadProposalV2}
                      className="download-button primary"
                    >
                      <Download />
                      Download PPTX
                    </button>
                  </div>
                  
                  {/* AI Edit Toggle - hidden */}
                  {/**
                  <div className="ai-edit-toggle-section">
                    <button
                      onClick={() => setAiEditEnabled(!aiEditEnabled)}
                      className={`ai-edit-toggle ${aiEditEnabled ? 'enabled' : ''}`}
                    >
                      <Zap />
                      {aiEditEnabled ? 'Disable AI Editing' : 'Enable AI Editing'}
                    </button>
                  </div>
                  **/}
                </div>

                {/* AI Chat Edit Interface - Only show when enabled */}
                {aiEditEnabled && (
                <div className="edit-interface">
                  <div className="edit-mode-toggle">
                    <button
                      onClick={() => setEditMode('ai')}
                      className={`mode-button ${editMode === 'ai' ? 'active' : ''}`}
                    >
                      🤖 AI Chat Edit
                    </button>
                    <button
                      onClick={() => setEditMode('manual')}
                      className={`mode-button ${editMode === 'manual' ? 'active' : ''}`}
                    >
                      ✏️ Manual Edit
                    </button>
                  </div>

                  {editMode === 'ai' ? (
                    <div className="ai-edit-panel">
                      <div className="ai-chat-section">
                        <div className="chat-header">
                          <h5>🤖 AI Content Editor</h5>
                          <p>Ask me to modify, improve, or restructure the content</p>
                        </div>
                        
                        <div className="chat-history">
                          {aiChatHistory.length === 0 ? (
                            <div className="chat-welcome">
                              <p>👋 Hi! I can help you edit the proposal content.</p>
                            </div>
                          ) : (
                            aiChatHistory.map((message, index) => (
                              <div key={index} className={`chat-message ${message.role}`}>
                                <div className="message-avatar">
                                  {message.role === 'user' ? '👤' : '🤖'}
                                </div>
                                <div className="message-content">
                                  <pre>{message.content}</pre>
                                </div>
                              </div>
                            ))
                          )}
                          {isAiProcessing && (
                            <div className="chat-message assistant">
                              <div className="message-avatar">🤖</div>
                              <div className="message-content">
                                <div className="typing-indicator">
                                  <span></span><span></span><span></span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="chat-input-area">
                          <div className="chat-input-wrapper">
                            <input
                              type="text"
                              value={aiChatInput}
                              onChange={(e) => setAiChatInput(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleAiChatSubmit()}
                              placeholder="Ask me to edit the content... (e.g., 'Make it more concise')"
                              disabled={isAiProcessing}
                              className="chat-input"
                            />
                            <button
                              onClick={handleAiChatSubmit}
                              disabled={isAiProcessing || !aiChatInput.trim()}
                              className="chat-send-button"
                            >
                              {isAiProcessing ? '⏳' : '➤'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="manual-edit-panel">
                      <div className="manual-editor">
                        <div className="editor-toolbar">
                          <h5>✏️ Manual Content Editor</h5>
                          <div className="editor-stats">
                            <span className="char-count">{editedContent.length.toLocaleString()} characters</span>
                            <span className="word-count">{editedContent.split(' ').length.toLocaleString()} words</span>
                          </div>
                        </div>
                        <textarea
                          value={editedContent}
                          onChange={(e) => setEditedContent(e.target.value)}
                          className="manual-content-editor"
                          placeholder="Edit your Stage 2 content here..."
                        />
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposalDashboard;
